/**
 * ROADMAP JOURNAL SERVICE
 *
 * Service layer for Roadmap Journal CRUD operations and media uploads
 * Uses API endpoints for all service role operations - NO getServiceRoleClient() in browser
 */

import { getSessionHeaders } from './serverSession';
import type {
 RoadmapJournalEntry,
 RoadmapJournalMedia,
 CreateJournalEntryRequest,
 UpdateJournalEntryRequest,
 CreateJournalMediaRequest,
 UpdateJournalMediaRequest,
 JournalQueryOptions,
 JournalStatistics
} from '../types/roadmapJournal';

const __STORAGE_BUCKET = 'training-assets';
const STORAGE_PATH = 'roadmap-journal';
const USER_JOURNAL_API = '/api/user/roadmap-journal';
const ADMIN_JOURNAL_API = '/api/admin/roadmap-journal';

// API helper for journal operations - NO getServiceRoleClient() in browser
async function callJournalAPI(
 action: string,
 params: Record<string, any> = {},
 endpoint: string = ADMIN_JOURNAL_API
): Promise<any> {
 const response = await fetch(endpoint, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ action, ...params })
 });
 return response.json();
}

export class RoadmapJournalService {
 // ============ ENTRY OPERATIONS ============

 /**
 * Get all journal entries (with optional filters)
 */
 static async getEntries(options: JournalQueryOptions = {}): Promise<RoadmapJournalEntry[]> {
 // Shareholders read published entries via the user API. Admin manager
 // passes onlyPublished: false and stays on the admin endpoint.
 const endpoint = options.onlyPublished === false ? ADMIN_JOURNAL_API : USER_JOURNAL_API;
 const result = await callJournalAPI('getEntries', options, endpoint);
 if (result.error) throw new Error(`Failed to fetch entries: ${result.error}`);
 return result.data || [];
 }

 /**
 * Get a single published entry by ID (shareholder-safe)
 */
 static async getEntryById(id: string): Promise<RoadmapJournalEntry | null> {
 const result = await callJournalAPI('getEntryById', { id }, USER_JOURNAL_API);
 if (result.error) throw new Error(`Failed to fetch entry: ${result.error}`);
 return result.data;
 }

 /**
 * Create a new journal entry
 */
 static async createEntry(entry: CreateJournalEntryRequest): Promise<RoadmapJournalEntry> {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callJournalAPI('createEntry', { entry });
 if (result.error) throw new Error(`Failed to create entry: ${result.error}`);
 return result.data;
 }

 /**
 * Update a journal entry
 */
 static async updateEntry(entry: UpdateJournalEntryRequest): Promise<RoadmapJournalEntry> {
 const { id, ...updateData } = entry;
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callJournalAPI('updateEntry', { id, updateData });
 if (result.error) throw new Error(`Failed to update entry: ${result.error}`);
 return result.data;
 }

 /**
 * Delete a journal entry and all associated media
 */
 static async deleteEntry(id: string): Promise<void> {
 // Use API endpoint - NO getServiceRoleClient() in browser
 // API handles deletion of media files from storage
 const result = await callJournalAPI('deleteEntry', { id });
 if (result.error) throw new Error(`Failed to delete entry: ${result.error}`);
 }

 // ============ MEDIA OPERATIONS ============

 /**
 * Add media to an entry
 */
 static async addMedia(media: CreateJournalMediaRequest): Promise<RoadmapJournalMedia> {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callJournalAPI('addMedia', { media });
 if (result.error) throw new Error(`Failed to add media: ${result.error}`);
 return result.data;
 }

 /**
 * Update media item
 */
 static async updateMedia(media: UpdateJournalMediaRequest): Promise<RoadmapJournalMedia> {
 const { id, ...updateData } = media;
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callJournalAPI('updateMedia', { id, updateData });
 if (result.error) throw new Error(`Failed to update media: ${result.error}`);
 return result.data;
 }

 /**
 * Delete a media item
 */
 static async deleteMedia(id: string): Promise<void> {
 // Use API endpoint - NO getServiceRoleClient() in browser
 // API handles deletion of storage files
 const result = await callJournalAPI('deleteMedia', { id });
 if (result.error) throw new Error(`Failed to delete media: ${result.error}`);
 }

 /**
 * Reorder media items
 */
 static async reorderMedia(mediaItems: { id: string; display_order: number }[]): Promise<void> {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callJournalAPI('reorderMedia', { mediaItems });
 if (result.error) throw new Error(`Failed to reorder media: ${result.error}`);
 }

 // ============ FILE UPLOAD OPERATIONS ============

 /**
 * Helper to convert File to base64 for API transmission
 */
 private static async fileToBase64(file: File): Promise<string> {
 const arrayBuffer = await file.arrayBuffer();
 return btoa(
 new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
 );
 }

 /**
 * Upload a photo
 */
 static async uploadPhoto(file: File, entryId: string): Promise<RoadmapJournalMedia> {
 // Generate unique filename
 const fileExt = file.name.split('.').pop();
 const fileName = `photo-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
 const filePath = `${STORAGE_PATH}/photos/${fileName}`;

 // Convert file to base64 and upload via API - NO getServiceRoleClient() in browser
 const fileBase64 = await this.fileToBase64(file);
 const uploadResult = await callJournalAPI('uploadFile', {
 fileBase64,
 fileType: file.type,
 filePath
 });

 if (uploadResult.error) throw new Error(`Failed to upload photo: ${uploadResult.error}`);

 // Get image dimensions (client-side operation)
 const dimensions = await this.getImageDimensions(file);

 // Create media record via API
 return this.addMedia({
 entry_id: entryId,
 media_type: 'photo',
 file_url: uploadResult.data.publicUrl,
 file_name: file.name,
 file_size: file.size,
 width: dimensions.width,
 height: dimensions.height
 });
 }

 /**
 * Upload a video
 */
 static async uploadVideo(
 file: File,
 entryId: string,
 onProgress?: (progress: number) => void
 ): Promise<RoadmapJournalMedia> {
 // Generate unique filename
 const fileExt = file.name.split('.').pop();
 const fileName = `video-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
 const filePath = `${STORAGE_PATH}/videos/${fileName}`;

 // For progress tracking, we simulate progress since we're using base64 upload
 if (onProgress) onProgress(10);

 // Convert file to base64 and upload via API - NO getServiceRoleClient() in browser
 const fileBase64 = await this.fileToBase64(file);
 if (onProgress) onProgress(50);

 const uploadResult = await callJournalAPI('uploadFile', {
 fileBase64,
 fileType: file.type,
 filePath
 });

 if (uploadResult.error) throw new Error(`Failed to upload video: ${uploadResult.error}`);
 if (onProgress) onProgress(70);

 // Get video duration (client-side operation)
 const duration = await this.getVideoDuration(file);

 // Generate and upload thumbnail
 let thumbnailUrl: string | undefined;
 try {
 const thumbnailBlob = await this.generateVideoThumbnail(file);
 const thumbFileName = `thumb-${Date.now()}.jpg`;
 const thumbPath = `${STORAGE_PATH}/thumbnails/${thumbFileName}`;

 // Convert thumbnail to base64 and upload
 const thumbBase64 = await this.fileToBase64(new File([thumbnailBlob], thumbFileName, { type: 'image/jpeg' }));
 const thumbResult = await callJournalAPI('uploadFile', {
 fileBase64: thumbBase64,
 fileType: 'image/jpeg',
 filePath: thumbPath
 });

 if (!thumbResult.error) {
 thumbnailUrl = thumbResult.data.publicUrl;
 }
 } catch (e) {
 console.warn('Failed to generate thumbnail:', e);
 }

 if (onProgress) onProgress(90);

 // Create media record via API
 const result = await this.addMedia({
 entry_id: entryId,
 media_type: 'video',
 file_url: uploadResult.data.publicUrl,
 file_name: file.name,
 file_size: file.size,
 thumbnail_url: thumbnailUrl,
 duration
 });

 if (onProgress) onProgress(100);
 return result;
 }

 // ============ UTILITY METHODS ============

 private static async getImageDimensions(file: File): Promise<{ width: number; height: number }> {
 return new Promise((resolve) => {
 const img = new Image();
 img.onload = () => {
 resolve({ width: img.width, height: img.height });
 URL.revokeObjectURL(img.src);
 };
 img.onerror = () => resolve({ width: 0, height: 0 });
 img.src = URL.createObjectURL(file);
 });
 }

 private static async getVideoDuration(file: File): Promise<number> {
 return new Promise((resolve) => {
 const video = document.createElement('video');
 video.onloadedmetadata = () => {
 resolve(Math.round(video.duration));
 URL.revokeObjectURL(video.src);
 };
 video.onerror = () => resolve(0);
 video.src = URL.createObjectURL(file);
 });
 }

 private static async generateVideoThumbnail(file: File): Promise<Blob> {
 return new Promise((resolve, reject) => {
 const video = document.createElement('video');
 const canvas = document.createElement('canvas');
 const ctx = canvas.getContext('2d');

 video.onloadedmetadata = () => {
 canvas.width = video.videoWidth;
 canvas.height = video.videoHeight;
 video.currentTime = Math.min(5, video.duration / 2);
 };

 video.onseeked = () => {
 if (ctx) {
 ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
 canvas.toBlob((blob) => {
 URL.revokeObjectURL(video.src);
 if (blob) resolve(blob);
 else reject(new Error('Failed to generate thumbnail'));
 }, 'image/jpeg', 0.8);
 }
 };

 video.onerror = () => {
 URL.revokeObjectURL(video.src);
 reject(new Error('Failed to load video'));
 };

 video.src = URL.createObjectURL(file);
 });
 }

 private static async __deleteStorageFile(url: string): Promise<void> {
 // Use API endpoint - NO getServiceRoleClient() in browser
 try {
 await callJournalAPI('deleteStorageFile', { url });
 } catch (e) {
 console.warn('Failed to delete storage file:', e);
 }
 }

 /**
 * Get statistics for admin dashboard
 */
 static async getStatistics(): Promise<JournalStatistics> {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callJournalAPI('getStatistics');
 if (result.error) throw new Error(`Failed to get statistics: ${result.error}`);
 return result.data;
 }
}

